import { useState } from "react";
import { Link } from "react-router";
import { useCloseDeal, useCreateDeal, useSupplierDeals } from "../../api/deals.js";
import { AppShell } from "../../components/AppShell.js";
import { EmptyState } from "../../components/EmptyState.js";
import { Field, buttonClass, inputClass } from "../../components/Field.js";
import { DealStatusBadge } from "../../components/StatusBadge.js";
import { formatDateTime, formatMoney, formatQty } from "../../lib/format.js";

const EMPTY_FORM = { title: "", description: "", qtyAvailable: "", unitPrice: "", expiresAt: "" };

/** A deal without a SKU is free-text stock — treated as a plain whole-unit count for display. */
const unitOf = (deal: { sku: { unit: string; isDivisible: boolean } | null }) =>
  deal.sku ?? { unit: "개", isDivisible: false };

export function SupplierDealsPage() {
  const deals = useSupplierDeals();
  const createDeal = useCreateDeal();
  const closeDeal = useCloseDeal();
  const [form, setForm] = useState(EMPTY_FORM);
  // Closing a deal that still has un-finalized claims permanently forfeits invoicing for them
  // (a DB trigger blocks claim writes once the deal is no longer open) — so this id gates a
  // one-tap-away "are you sure" step rather than closing immediately.
  const [confirmCloseId, setConfirmCloseId] = useState<string | null>(null);

  return (
    <AppShell title="재고 특가 등록">
      <details className="mb-6 rounded-xl border border-stone-200 bg-white p-4">
        <summary className="cursor-pointer font-semibold">새 특가 등록</summary>
        <form
          className="mt-4"
          onSubmit={(e) => {
            e.preventDefault();
            createDeal.mutate(
              {
                skuId: null,
                title: form.title,
                description: form.description || null,
                qtyAvailable: form.qtyAvailable,
                unitPrice: form.unitPrice,
                expiresAt: new Date(form.expiresAt).toISOString(),
              },
              { onSuccess: () => setForm(EMPTY_FORM) },
            );
          }}
        >
          <Field label="제목" htmlFor="title">
            <input
              id="title"
              required
              className={inputClass}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </Field>
          <Field label="설명 (선택)" htmlFor="description">
            <textarea
              id="description"
              rows={3}
              className={inputClass}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </Field>
          <Field label="수량" htmlFor="qtyAvailable">
            <input
              id="qtyAvailable"
              inputMode="decimal"
              required
              className={inputClass}
              value={form.qtyAvailable}
              onChange={(e) => setForm({ ...form, qtyAvailable: e.target.value })}
            />
          </Field>
          <Field label="단가 (예: 39.00)" htmlFor="unitPrice">
            <input
              id="unitPrice"
              inputMode="decimal"
              required
              className={inputClass}
              value={form.unitPrice}
              onChange={(e) => setForm({ ...form, unitPrice: e.target.value })}
            />
          </Field>
          <Field label="마감 일시" htmlFor="expiresAt">
            <input
              id="expiresAt"
              type="datetime-local"
              required
              className={inputClass}
              value={form.expiresAt}
              onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
            />
          </Field>
          {createDeal.error ? (
            <p role="alert" className="mb-3 text-sm text-red-600">
              {(createDeal.error as Error).message}
            </p>
          ) : null}
          <button type="submit" className={buttonClass} disabled={createDeal.isPending}>
            특가 등록
          </button>
        </form>
      </details>

      {deals.data && deals.data.length === 0 ? <EmptyState message="등록한 특가가 없습니다." /> : null}

      <ul>
        {deals.data?.map((deal) => (
          <li key={deal.id} className="mb-3 rounded-xl border border-stone-200 bg-white p-4">
            <div className="mb-1 flex items-start justify-between gap-2">
              <h2 className="font-semibold">{deal.title}</h2>
              <DealStatusBadge status={deal.status} />
            </div>
            <p className="text-sm text-stone-600">
              {formatMoney(deal.unitPrice)} · 신청 {formatQty(deal.qtyClaimed, unitOf(deal))}/
              {formatQty(deal.qtyAvailable, unitOf(deal))} · 마감 {formatDateTime(deal.expiresAt)}
            </p>

            {deal.claimants.length > 0 ? (
              <ul className="mt-2 text-sm">
                {deal.claimants.map((claimant) => (
                  <li
                    key={claimant.claimId}
                    className="flex items-center justify-between border-t border-stone-100 py-1"
                  >
                    <span>{claimant.restaurantName}</span>
                    <span className="text-stone-600">{formatQty(claimant.qty, unitOf(deal))}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-2 text-sm text-stone-400">아직 신청한 식당이 없습니다.</p>
            )}

            {deal.status === "open" ? (
              <div className="mt-3 flex flex-col gap-2">
                {deal.claimants.length > 0 ? (
                  // Primary action: a deal with claims should be finalized, not just closed —
                  // closing first makes those claims permanently un-invoiceable.
                  <Link
                    to={`/supplier/deals/${deal.id}/finalize`}
                    className="rounded-lg bg-brand-700 px-4 py-3 text-center text-sm font-semibold text-white hover:bg-brand-800"
                  >
                    납품 확정 · 청구서 발행
                  </Link>
                ) : null}

                {confirmCloseId === deal.id ? (
                  <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
                    <p className="mb-2 text-sm text-amber-800">
                      마감하면 이 특가는 다시 납품 확정할 수 없고, 신청한 식당에 청구서를 발행할 수
                      없습니다. 정말 마감하시겠습니까?
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="flex-1 rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
                        disabled={closeDeal.isPending}
                        onClick={() => closeDeal.mutate(deal.id, { onSuccess: () => setConfirmCloseId(null) })}
                      >
                        마감 확정 (청구서 발행 안 함)
                      </button>
                      <button
                        type="button"
                        className="rounded-lg border border-stone-300 px-3 py-2 text-sm"
                        onClick={() => setConfirmCloseId(null)}
                      >
                        취소
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="rounded-lg border border-stone-300 px-3 py-2 text-sm text-stone-600 disabled:opacity-50"
                    disabled={closeDeal.isPending}
                    onClick={() => {
                      if (deal.claimants.length > 0) {
                        setConfirmCloseId(deal.id);
                      } else {
                        closeDeal.mutate(deal.id);
                      }
                    }}
                  >
                    마감 (청구서 발행 안 함)
                  </button>
                )}
              </div>
            ) : null}

            {closeDeal.error ? (
              <p role="alert" className="mt-2 text-sm text-red-600">
                {(closeDeal.error as Error).message}
              </p>
            ) : null}
          </li>
        ))}
      </ul>
    </AppShell>
  );
}
